import React, { useEffect, useState } from "react";
import { AiOutlineArrowRight, AiOutlineMoneyCollect } from "react-icons/ai";
import { Link } from "react-router-dom";
import { MdBorderClear } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { getAllProductsShop } from "../../redux/actions/product";
import { Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Loader from "../Layout/Loader";

const DashboardHero = () => {
  const dispatch = useDispatch();
  const { orders = [] } = useSelector((state) => state.order || {});
  const { seller = {} } = useSelector((state) => state.seller || {});
  const { products = [] } = useSelector((state) => state.products || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (seller._id) {
        setIsLoading(true);
        try {
          await Promise.all([
            dispatch(getAllOrdersOfShop(seller._id)),
            dispatch(getAllProductsShop(seller._id))
          ]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchData();
  }, [dispatch, seller._id]);

  const totalOrderAmount = orders.reduce(
    (acc, order) =>
      order.status !== "Cancelled" ? acc + (order.shopTotalOrderPriceWithoutCommission || 0) : acc,
    0
  );
  const availableBalance = (totalOrderAmount - (seller.withdrawnAmount || 0)).toFixed(2);

  const columns = [
    { field: "id", headerName: "Order ID", minWidth: 150, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 1,
      cellClassName: (params) => {
        return params.row.status === "Delivered" ? "text-green-600 font-semibold" : "text-gray-600 font-semibold";
      },
    },
    {
      field: "itemsQty",
      headerName: "Items Qty",
      type: "number",
      minWidth: 130,
      flex: 1,
    },
    {
      field: "total",
      headerName: "Total",
      type: "number",
      minWidth: 130,
      flex: 1,
      renderCell: (params) => `INR Rs ${params.value}`,
    },
    {
      field: "action",
      headerName: "",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        return (
          <Link to={`/order/${params.id}`}>
            <Button variant="contained" color="primary" className="flex items-center justify-center">
              <AiOutlineArrowRight size={20} />
            </Button>
          </Link>
        );
      },
    },
  ];

  const rows = orders.map((item) => ({
    id: item._id,
    itemsQty: item.cart.reduce((acc, item) => acc + item.qty, 0),
    total: item.shopTotalOrderPriceWithoutCommission,
    status: item.status,
  }));

  return (
    <div className="w-full p-4 sm:p-8 bg-gray-50 min-h-screen">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Overview Section */}
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Overview</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        {/* Total Sales Card */}
        <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AiOutlineMoneyCollect size={30} className="text-green-500 mr-3" />
            <h3 className="text-lg font-medium text-gray-700">Total Sales in Hawler</h3>
          </div>
          <h5 className="text-2xl font-bold text-gray-800 mb-2">
            INR Rs {isNaN(availableBalance) ? "0.00" : availableBalance}
          </h5>
          <Link to={`/dashboard-shop-account-transactions/${seller._id}`} className="text-blue-600 hover:underline mt-auto">
            View Transactions / Balance
          </Link>
        </div>

        {/* All Orders Card */}
        <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center mb-4">
            <MdBorderClear size={30} className="text-blue-500 mr-3" />
            <h3 className="text-lg font-medium text-gray-700">All Orders</h3>
          </div>
          <h5 className="text-2xl font-bold text-gray-800 mb-2">{orders.length}</h5>
          <Link to="/dashboard-orders" className="text-blue-600 hover:underline mt-auto">
            View Orders
          </Link>
        </div>

        {/* All Products Card */}
        <div className="flex flex-col bg-white shadow-md rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AiOutlineMoneyCollect size={30} className="text-purple-500 mr-3" />
            <h3 className="text-lg font-medium text-gray-700">All Products</h3>
          </div>
          <h5 className="text-2xl font-bold text-gray-800 mb-2">{products.length}</h5>
          <Link to="/dashboard-products" className="text-blue-600 hover:underline mt-auto">
            View Products
          </Link>
        </div>
      </div>

      {/* Latest Orders Section */}
      <div>
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">Latest Orders</h3>
        <div className="bg-white shadow-md rounded-lg p-4">
          <div className="datagrid-scroll-wrapper">
            <div className="table-inner-container" style={{ minWidth: "700px" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                disableSelectionOnClick
                autoHeight
                className="bg-white"
                getRowClassName={(params) =>
                  params.row.status === "Delivered" ? "bg-green-50" : "bg-white"
                }
                components={{
                  NoRowsOverlay: () => (
                    <div className="flex justify-center items-center h-full">
                      <span className="text-gray-500">No orders available.</span>
                    </div>
                  ),
                }}
                // Responsive adjustments
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": {
                    padding: "8px",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f3f4f6",
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                  "& .MuiButton-root": {
                    padding: "6px 12px",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "visible !important",
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default DashboardHero;

